import {
  type ErrorHandlerFunction,
  type HandledResponse,
  type ApiErrorUnion,
  BlobApiResponse,
  FetchError,
  GetErrorResponses,
  JSONApiResponse,
  ResponseError,
  ErrorResponse,
  UnknownError,
  VoidApiResponse,
} from '@/api/generator/runtime';
import { flowAsync, type FlowFunction } from './flowAsync';
import { usePermissionsStore } from '@/stores/permissionsStore';

/**
 * Adapts the codegen's `ErrorHandlerResult` contract (`Error | void`) to `flowAsync`'s
 * `FlowFunction<E>` contract (`E | undefined`).
 *
 * The `result as E` cast cannot be removed: `E` is erased at runtime, so nothing can prove a
 * returned `Error` is this call's `E`. Handlers are expected to return the error they were
 * given or an `E`-compatible one.
 */
function toFlowFunction<E extends Error>(handler: ErrorHandlerFunction<E>): FlowFunction<E> {
  return async (error) => {
    const result = await handler(error);
    return result === undefined ? undefined : (result as E);
  };
}

export function errorFlowAsync<E extends Error>(...errorHandler: Array<ErrorHandlerFunction<E>>) {
  return flowAsync<E>(...errorHandler.map(toFlowFunction));
}

/**
 * The `error` result is typed as the per-endpoint union of names that operation's declared
 * responses can produce, plus `FetchError`/`UnknownError` - not the application-wide
 * `AnyApiError`. Switch on `error.name` at the call site to narrow to just those.
 *
 * A handler that throws is reported to the console and cannot replace the original error or
 * re-run the chain, so handlers never need defensive try/catch of their own.
 *
 * @example
 * const { success, result, error } = await api.entityLockTokensApi.updateToken({ tokenId });
 * if (!success) {
 *   switch (error.name) {
 *     case 'ErrorResponseOfPtw100ConflictException':
 *       // error.error is narrowed to ErrorResponseOfPtw100ConflictException here
 *       break;
 *     case 'FetchError':
 *     case 'UnknownError':
 *       // no backend-declared body for these - handle as a generic failure
 *       break;
 *   }
 * }
 */
export async function handle<
  S,
  R extends Promise<JSONApiResponse | VoidApiResponse | BlobApiResponse>,
>(
  promise: R,
  errorHandlers: Array<ErrorHandlerFunction<ApiErrorUnion<R, S>>> = [],
): Promise<HandledResponse<S, R>> {
  let error: ApiErrorUnion<R, S>;

  try {
    const response = await promise;

    const permissionsUpdated = response.raw.headers.get('Userrolepermissions') === 'Updated';
    if (permissionsUpdated) await usePermissionsStore().fetchMyPermissions();

    if (response && response.raw.status >= 200 && response.raw.status < 300) {
      return {
        success: true,
        result: (await response.value()) as S,
        error: undefined,
        raw: response.raw,
      };
    }

    // Unavoidable: `ResponseError.create` is a generic static factory, so TS cannot recover
    // the caller-scoped `R`/`S` arguments from the constructed value.
    error = (await ResponseError.create(response)) as ErrorResponse<GetErrorResponses<R, S>>;
  } catch (e) {
    if (e instanceof ResponseError) {
      // `instanceof` proves the runtime class only, not this call's generic arguments.
      error = e as ErrorResponse<GetErrorResponses<R, S>>;
    } else if (e instanceof FetchError) {
      error = e;
    } else {
      error = new UnknownError(e as Error);
    }
  }

  try {
    await errorFlowAsync(...errorHandlers)(error);
  } catch (handlerFailure) {
    console.error(
      'An API error handler threw. The original error is still reported.',
      handlerFailure,
    );
  }

  return {
    success: false,
    result: undefined,
    error,
    raw: error instanceof ResponseError ? error.response.clone() : undefined,
  };
}
